import { IOrganization } from '@core/services/organization/organization.interface';
import { ConfigFacadeService } from '@core/services/config/config-facade.service';
import { OrganizationsService } from '@app/services/organizations.service';
import { RestrictedSupportOrganizations } from '@app/support-request';
import { PartnerAliasEnum } from '@partners/partner-alias.enum';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { skipWhile, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { UserService } from '@app/services/user/user.service';
import { MatDialog } from '@angular/material/dialog';
import { SuccessSupportModalComponent, SupportModalComponent } from '@app/support-request';

@Injectable({
	providedIn: 'root',
})
export class SupportModalService {
	private _isSupportAvailable$: BehaviorSubject<boolean> = new BehaviorSubject(null);
	private _showRequest$: Subject<void> = new Subject<void>();
	private currentUserOrganization: IOrganization;

	constructor(
		private dialog: MatDialog,
		private readonly organizationsService: OrganizationsService,
		private readonly userService: UserService,
		private readonly configFacade: ConfigFacadeService,
	) {}

	public init(): void {
		this.organizationsService
			.getCurrentUserOrganization()
			.pipe(take(1))
			.subscribe((organization: IOrganization) =>
				this._isSupportAvailable$.next(this.defineIsSupportAvailable(organization)),
			);
	}

	public showFromHost(): void {
		const supportDialogRef = this.dialog.open(SupportModalComponent, {
			width: '557px',
			height: '645px',
			data: {
				userName: this.userService.userNickname,
				userEmail: this.userService.userEmail,
				orgName: this.isPartnerSupportForm
					? `(${this.configFacade.partnerName})`
					: this.currentUserOrganization.org_name,
			},
		});
		supportDialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe((isSubmitted) => {
				if (isSubmitted) {
					this.dialog.open(SuccessSupportModalComponent, {
						width: '431px',
						height: '370px',
						data: {
							userEmail: this.userService.userEmail,
						},
					});
				}
			});
	}

	public show(): void {
		this._showRequest$.next();
	}

	public get showRequest$(): Observable<void> {
		return this._showRequest$.asObservable();
	}

	public get isSupportAvailable$(): Observable<boolean> {
		return this._isSupportAvailable$.asObservable().pipe(skipWhile((x) => x === null));
	}

	public defineIsSupportAvailable(organization: IOrganization): boolean {
		this.currentUserOrganization = organization;
		if (
			this.configFacade.partnerPrefix === PartnerAliasEnum.Zesty &&
			!(organization.org_id in RestrictedSupportOrganizations) &&
			!organization.reseller_managed
		) {
			return true;
		}
		return false;
	}

	private isPartnerSupportForm(): boolean {
		return !!this.configFacade.partnerId && this.configFacade.partnerPrefix !== PartnerAliasEnum.Zesty;
	}
}
